import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMold } from '../hooks/useMold';
import MainContainer from '../components/layout/MainContainer';
import MoldDetail from '../components/MoldDetail';
import MoldDetailSkeleton from '../components/MoldDetail/MoldDetailSkeleton';
import ErrorPage from '../components/pages/Error';
import { useBreadcrumbs } from '../hooks/useBreadcrumbs';
import { useAppState } from '@/context/AppStateContext';

interface MoldDetailContainerProps { }

const MoldDetailContainer: React.FC<MoldDetailContainerProps> = () => {
  const { id } = useParams<{ id: string }>();
  const {
    mold,
    editedMold,
    isEditing,
    loading,
    error,
    fieldErrors,
    fetchMold,
    handleEditMode,
    handleCancelEdit,
    handleSaveEdit,
    handleFieldChange,
    associatePart: originalAssociatePart,
    associateInsert: originalAssociateInsert,
    deleteAssociatedPart: originalDeleteAssociatedPart,
    deleteAssociatedInsert: originalDeleteAssociatedInsert,
  } = useMold();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { getMoldTitle } = useAppState();
  const moldTitle = mold ? `แม่พิมพ์ ${getMoldTitle(mold)}` : 'หน้าแม่พิมพ์';
  const { breadcrumbItems, showBackButton, onBack } = useBreadcrumbs({
    currentPageTitle: moldTitle,
  });

  useEffect(() => {
    if (id) {
      fetchMold(id);
    }
  }, [id, fetchMold]);

  const associatePart = async (moldId: string, partId: number) => {
    await originalAssociatePart(moldId, partId);
    fetchMold(moldId).then(() => {
      setSuccessMessage(`เพิ่มชิ้นส่วนที่เกี่ยวข้องสำเร็จ!`);
    });
  };

  const associateInsert = async (moldId: string, insertId: number) => {
    await originalAssociateInsert(moldId, insertId);
    fetchMold(moldId).then(() => {
      setSuccessMessage(`เพิ่มอินเสิร์ทที่เกี่ยวข้องสำเร็จ!`);
    }
    );
  };

  const deleteAssociatedPart = async (moldId: string, partId: number) => {
    await originalDeleteAssociatedPart(moldId, partId);
    fetchMold(moldId); // Refresh mold data
  };

  const deleteAssociatedInsert = async (moldId: string, insertId: number) => {
    await originalDeleteAssociatedInsert(moldId, insertId);
    fetchMold(moldId); // Refresh mold data
  };

  if (loading)
    return (
      <MainContainer
        breadcrumbItems={breadcrumbItems}
        showBackButton={showBackButton}
        onBack={onBack}
      >
        <MoldDetailSkeleton />
      </MainContainer>
    );
  if (!mold) return <ErrorPage
    title="ขออภัย ไม่พบข้อมูลแม่พิมพ์ที่ต้องการค้นหา"
    message=''
    errorCode={404}
  />;
  // Only show the full error page for fetch errors (not save/validation errors while editing)
  if (error && !isEditing) return <ErrorPage />;

  return (
    <MainContainer
      title="ข้อมูลแม่พิมพ์"
      breadcrumbItems={breadcrumbItems}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <MoldDetail
        mold={mold}
        editedMold={editedMold!}
        isEditing={isEditing}
        loading={loading}
        error={error}
        fieldErrors={fieldErrors}
        onEdit={handleEditMode}
        onCancel={handleCancelEdit}
        onSave={handleSaveEdit}
        onFieldChange={handleFieldChange}
        associatePart={associatePart}
        associateInsert={associateInsert}
        deleteAssociatedInsert={deleteAssociatedInsert}
        deleteAssociatedPart={deleteAssociatedPart}
        successMessage={successMessage} // Pass success message as prop
      />
    </MainContainer>
  );
};

export default MoldDetailContainer;
